/**
 * Servizio Firestore per il database piste condiviso (re_tracks).
 *
 * Collezione GLOBALE — non sotto nessun team.
 * Contiene SOLO geometria e metadati tecnici della pista.
 * Nessun dato di performance (tempi, setup, feedback).
 *
 * re_tracks/{track_slug}
 *   name:         string
 *   country:      string (default "IT")
 *   length_m:     number (opzionale)
 *   gps_trace:    [{t, lat, lon, v}]   ← campionamento GPS per la mappa
 *   sectors: [{
 *     sector:              number
 *     lat_start:           number
 *     lon_start:           number
 *     lat_mid:             number
 *     lon_mid:             number
 *     nome:                string      ← editabile
 *     tipo:                string      ← hairpin|chicane|sweeper|complex|rettilineo
 *     banking_deg:         number      ← negativo = contropendenza
 *     velocita:            string      ← lenta|media|veloce
 *     precede_rettilineo:  boolean
 *     note:                string
 *     contributions:       number
 *   }]
 *   contributions:  number   ← sessioni totali caricate su questa pista
 *   created_at:     timestamp
 *   updated_at:     timestamp
 */

import {
  doc, getDoc, setDoc, updateDoc, getDocs,
  collection, serverTimestamp, increment,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const COLLECTION = 're_tracks';

/** Normalizza il nome pista in slug (es. "Castelletto di Branduzzo" → "castelletto_branduzzo") */
export function trackNameToSlug(name) {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')  // rimuovi accenti
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/** Recupera una pista dal database globale. Restituisce null se non esiste. */
export async function getReTrack(slug) {
  const ref = doc(db, COLLECTION, slug);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Crea o aggiorna la geometria di una pista dopo un'analisi.
 * Non sovrascrive i metadati editati manualmente (nome, tipo, banking, note).
 */
export async function upsertReTrackFromAnalysis(slug, trackName, trackData) {
  const ref = doc(db, COLLECTION, slug);
  const snap = await getDoc(ref);

  const gpsTrace = (trackData.track_gps || []).slice(0, 600); // max 600 punti

  if (!snap.exists()) {
    // Prima volta — crea il record base
    const sectors = (trackData.sectors || []).map(s => ({
      sector:             s.sector,
      lat_start:          s.lat_start  ?? null,
      lon_start:          s.lon_start  ?? null,
      lat_mid:            s.lat_mid    ?? null,
      lon_mid:            s.lon_mid    ?? null,
      nome:               `Settore ${s.sector}`,
      tipo:               '',
      banking_deg:        0,
      velocita:           '',
      precede_rettilineo: false,
      note:               '',
      contributions:      1,
    }));

    await setDoc(ref, {
      name:          trackName,
      country:       'IT',
      gps_trace:     gpsTrace,
      sectors,
      contributions: 1,
      created_at:    serverTimestamp(),
      updated_at:    serverTimestamp(),
    });
  } else {
    // Aggiorna solo GPS trace e contributions — preserva metadati editati
    await updateDoc(ref, {
      gps_trace:     gpsTrace,
      contributions: increment(1),
      updated_at:    serverTimestamp(),
    });

    // Aggiorna lat/lon dei settori (geometria) senza toccare i metadati
    const existing = snap.data();
    const existingSectors = existing.sectors || [];
    const newSectors = (trackData.sectors || []);

    // 1. Aggiorna i settori già presenti
    const merged = existingSectors.map(es => {
      const ns = newSectors.find(s => s.sector === es.sector);
      if (!ns) return es;
      return {
        ...es,
        lat_start:     ns.lat_start  ?? es.lat_start,
        lon_start:     ns.lon_start  ?? es.lon_start,
        lat_mid:       ns.lat_mid    ?? es.lat_mid,
        lon_mid:       ns.lon_mid    ?? es.lon_mid,
        contributions: (es.contributions || 0) + 1,
      };
    });

    // 2. Aggiunge i settori NUOVI che non erano ancora nel documento
    //    (es. prima analisi con 13 split, ora con 18 → aggiunge S14-S18)
    const existingNums = new Set(existingSectors.map(s => s.sector));
    const added = newSectors
      .filter(ns => !existingNums.has(ns.sector))
      .map(ns => ({
        sector:             ns.sector,
        lat_start:          ns.lat_start  ?? null,
        lon_start:          ns.lon_start  ?? null,
        lat_mid:            ns.lat_mid    ?? null,
        lon_mid:            ns.lon_mid    ?? null,
        nome:               `Settore ${ns.sector}`,
        tipo:               '',
        banking_deg:        0,
        velocita:           '',
        precede_rettilineo: false,
        note:               '',
        contributions:      1,
      }));

    const fullMerged = [...merged, ...added].sort((a, b) => a.sector - b.sector);
    await updateDoc(ref, { sectors: fullMerged });
  }
}

/**
 * Inizializza re_tracks da database circuiti (solo se non esiste già).
 * Chiamato quando si crea una pista dal database: popola la GPS trace
 * così la mappa funziona subito senza dover caricare un CSV.
 */
export async function initReTrackFromDatabase(slug, trackName, circuitData) {
  if (!circuitData?.gps_trace?.length) return;
  const ref  = doc(db, 're_tracks', slug);
  const snap = await getDoc(ref);

  // Sovrascrive sempre quando chiamata esplicitamente dal database:
  // l'utente ha scelto questa pista → vogliamo i dati corretti.
  // Preserva i settori editati manualmente se già presenti.
  const existingSectors = snap.exists() ? (snap.data().sectors || []) : [];

  const gpsTrace = circuitData.gps_trace
    .slice(0, 600)
    .map(p => ({ lat: p.lat, lon: p.lon }));

  await setDoc(ref, {
    name:          trackName,
    country:       circuitData.country || 'XX',
    gps_trace:     gpsTrace,
    sectors:       existingSectors,
    contributions: snap.exists() ? (snap.data().contributions || 0) : 0,
    source:        'circuit_db',
    created_at:    snap.exists() ? snap.data().created_at : serverTimestamp(),
    updated_at:    serverTimestamp(),
  });
}

/** Aggiorna i metadati di un singolo settore (nome, tipo, banking, note…) */
export async function updateReTrackSector(slug, sectorIndex, metadata) {
  const ref  = doc(db, COLLECTION, slug);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const sectors = [...(snap.data().sectors || [])];
  if (sectorIndex < 0 || sectorIndex >= sectors.length) return;

  sectors[sectorIndex] = {
    ...sectors[sectorIndex],
    ...metadata,
  };

  await updateDoc(ref, { sectors, updated_at: serverTimestamp() });
}

/** Aggiorna tutti i settori in una volta (salvataggio bulk dall'editor).
 *  Usa setDoc merge: crea il documento se non esiste ancora. */
export async function saveReTrackSectors(slug, sectors, name = '') {
  const ref = doc(db, COLLECTION, slug);
  await setDoc(ref, {
    sectors,
    updated_at: serverTimestamp(),
    ...(name ? { name } : {}),
  }, { merge: true });
}

/** Cerca una pista per prossimità GPS (carica tutti i doc e trova il più vicino).
 *  maxDistDeg ~0.3 = ~30km. */
export async function getReTrackByProximity(lat, lon, maxDistDeg = 0.3) {
  const snap = await getDocs(collection(db, COLLECTION));
  let best = null, bestDist = Infinity;
  snap.forEach(docSnap => {
    const data = docSnap.data();
    const gps  = data.gps_trace;
    if (!gps?.length) return;
    // Centro approssimativo della pista
    const latC = gps.reduce((s, p) => s + p.lat, 0) / gps.length;
    const lonC = gps.reduce((s, p) => s + p.lon, 0) / gps.length;
    const d = Math.sqrt((lat - latC) ** 2 + (lon - lonC) ** 2);
    if (d < bestDist && d < maxDistDeg) {
      bestDist = d;
      best = { id: docSnap.id, ...data };
    }
  });
  return best;
}
